import React from 'react';
import { Connection, ConnectionSource, ConnectionState } from '../types';
import styled from 'styled-components';
import { Text, Icon } from 'bench-internal-theme';
import { colors } from 'bench-style-constants';
import startCase from 'lodash/startCase';

const CardHeaderHeading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const CardHeaderCheckIcon = styled(Icon)`
  margin-left: 4px;
`;

const CardHeaderLayout = styled.div`
  display: grid;
  gap: 8px;
`;

type ConnectionCardHeaderProps = {
  connection: Connection;
};

const ConnectionCardHeader = ({ connection }: ConnectionCardHeaderProps) => {
  const isStripeConnection = connection.source === ConnectionSource[ConnectionSource.STRIPE];
  const isSquareConnection = connection.source === ConnectionSource[ConnectionSource.SQUARE];
  const isShopifyConnection = connection.source === ConnectionSource[ConnectionSource.SHOPIFY];
  const isPayPalConnection = connection.source === ConnectionSource[ConnectionSource.PAYPAL];
  const isAmazonConnection = connection.source === ConnectionSource[ConnectionSource.AMAZON];
  const hasAccounts = connection.accounts.length > 0;
  const formattedSource = startCase(connection.source.toLowerCase());

  const formatSource = () => {
    if (isShopifyConnection && connection.sourceId) {
      return `Source: ${formattedSource} (${connection.sourceId})`;
    }

    if (isSquareConnection && connection.sourceId) {
      return `Source: ${formattedSource} (Merchant ID: ${connection.sourceId})`;
    }

    if (isStripeConnection && connection.sourceId) {
      return `Source: ${formattedSource} (Account ID: ${connection.sourceId})`;
    } else if (isPayPalConnection || isAmazonConnection) {
      return `Source: ${formattedSource} - ${connection.accounts
        .map(account => account.defaultCurrency)
        .sort()
        .join(', ')}`;
    }

    return `Source: ${formattedSource}`;
  };
  const formatDateTime = (date: string) =>
    new Date(date).toLocaleString('en-US', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
    });

  const formattedHeading = (() => {
    if (isStripeConnection && hasAccounts) {
      return `${connection.financialInstitutionName} - ${connection.accounts[0].name}`;
    } else if (isAmazonConnection && hasAccounts) {
      return connection.accounts[0].name?.slice(0, -6);
    }
    return connection.financialInstitutionName;
  })();

  const getStoreUrl = (connectionName: string) => {
    const splitConnection = connectionName.split(' - ');
    return `https://www.amazon.${splitConnection[2]}/sp?seller=${splitConnection[1]}`;
  };

  return (
    <CardHeaderLayout>
      <CardHeaderHeading>
        <Text styleType="largeBold">{formattedHeading}</Text>
        {connection.state === ConnectionState[ConnectionState.CONNECTED] && (
          <CardHeaderCheckIcon
            size="18px"
            color={colors.bonsai}
            icon="circleCheck"
            title="Checkmark"
          />
        )}
        {isAmazonConnection && hasAccounts && formattedHeading && (
          <a
            href={getStoreUrl(formattedHeading?.toLowerCase() || '')}
            target="_blank"
            rel="noreferrer"
          >
            <Icon color={colors.bonsai} icon="link" size="small" />
          </a>
        )}
      </CardHeaderHeading>
      {connection.isSyncing && (
        <Text styleType="small" color={colors.cloud}>
          Fetch requested.
        </Text>
      )}
      {connection.source && (
        <Text styleType="small" color={colors.cloud}>
          {formatSource()}
        </Text>
      )}
      {isStripeConnection && hasAccounts && connection.accounts[0].defaultCurrency && (
        <Text
          styleType="small"
          color={colors.cloud}
        >{`Currency: ${connection.accounts[0].defaultCurrency}`}</Text>
      )}
      {hasAccounts && connection.accounts[0].isMultiCurrency && (
        <Text styleType="small" color={colors.cloud}>
          This connection uses multiple currencies and may require manual posting using Preview
          Transactions.
        </Text>
      )}
      {connection.accounts[0]?.sourceSyncDateTime && (
        <Text styleType="small" color={colors.cloud}>
          {`Last Plaid Bank Sync: ${formatDateTime(connection.accounts[0]?.sourceSyncDateTime)}`}
        </Text>
      )}
    </CardHeaderLayout>
  );
};

export default ConnectionCardHeader;
